import { I18NDef } from '../types/autogen/I18NDef';
import i18nRawDefinitions from '../types/autogen/i18nRawDefinitions.json';
import { replaceValuesWithTreeLocation, StringTree } from './stringtree.utils';

/**
 * A typed definition of all i18n keys, where the value of each key is the path/location of the key in the overall tree
 * of keys.
 *
 * Example:
 * t(i18nKeys.home.title) is the equivalent of t('home.title') but with type safety and autocompletion
 */
export const i18nKeys: I18NDef = replaceValuesWithTreeLocation(i18nRawDefinitions as I18NDef & StringTree) as I18NDef &
  StringTree;

export const I18N_PREFIX = 'i18n.';

/**
 * Matches a do-not-translate marker `[[literal]]` and captures the literal text between
 * the double square brackets. Global + non-greedy so multiple markers in one string are
 * each stripped independently.
 *
 * This is the runtime counterpart to the marker handling in scripts/localize_clean.js:
 * authors wrap proper nouns / service names that must never be translated in `[[ ]]`
 * (e.g. `[[AWS Jam]]`). The offline script strips the brackets when generating non-English
 * locales; this regex strips them at render time for the English source (and defensively
 * for any locale).
 */
export const DO_NOT_TRANSLATE_MARKER_REGEX = /\[\[([\s\S]*?)\]\]/g;

/**
 * Strips do-not-translate markers from a string, leaving the bare literal text.
 * `Welcome to [[AWS Jam]]` becomes `Welcome to AWS Jam`. Strings without markers are
 * returned unchanged.
 *
 * @param value - the (already interpolated) string to strip markers from
 */
export const stripDoNotTranslateMarkers = (value: string): string => {
  if (!value || value.indexOf('[[') === -1) {
    return value;
  }
  return value.replace(DO_NOT_TRANSLATE_MARKER_REGEX, (_match, inner) => inner);
};

/**
 * Prefix an i18n key with 'i18n' so that it can clearly be identified as an i18n key vs just a random string.
 *
 * @param key
 */
export const withI18NPrefix = (key: string): string => {
  return `${I18N_PREFIX}${key}`;
};

/**
 * Remove the prefix of an i18n key with 'i18n' so that it can clearly be identified as an i18n key vs just a random string.
 *
 * @param key
 */
export const withoutI18nPrefix = (key: string): string => {
  return key.startsWith(I18N_PREFIX) ? key.replace(I18N_PREFIX, '') : key;
};

/**
 * Generates a currency string in the format required so that it is displayed
 * properly when translated.
 *
 * @param currencySymbol
 * @param value
 */
export const generateFormatForCurrency = (currencySymbol: string, value: number): string => {
  // The space characters are intentional. They are needed to maintain formatting.
  return ` ${currencySymbol}${value} `;
};

export const pluralize = (singular: string, plural: string, amount: number) => {
  if (amount === 1) {
    return singular;
  }

  return plural;
};
